import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ButtonBase,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { bedStatusAPI } from '../../../../../apis';
import { AddIcon, CheckIcon, EditIcon } from '../../../../../components/Icon';

import { i18n } from '../../../../../language';
import { BED_STATUS_REMARK_COLOR } from '../../../../../comm/constant';
import EditRemark from '../../Popups/EditRemark';
import AddRemark from '../../Popups/AddRemark';
import useUpdateEffect from '../../../../../utils/useUpdateEffect';
const useStyles = makeStyles((theme) => ({
  root: {
    background: '#fff',
    padding: '1.875rem',
    borderRadius: '0 0.93rem 0.93rem 0.93rem',
  },
  bedStatusSetting: {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    lineHeight: '1',
    color: '#2e4469',
  },
  tableContainer: {
    height: '65vh',
  },
  table: {
    position: 'relative',
    minWidth: '45rem',
    '& .MuiTableCell-root': {
      border: 'none',
      fontSize: '1.125rem',
      fontWeight: '500',
      lineHeight: '1.675rem',
      color: '#2e4469',
      padding: '0 1rem ',
    },
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: '0',
      left: '0',
      zIndex: '10',
      '& .MuiTableRow-root': {
        backgroundColor: '#fff',
        textTransform: 'uppercase',
      },
      '& .MuiTableCell-root': {
        fontWeight: 'bold',
        color: '#538fff',
        padding: '1rem',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
      },
      '& .MuiTableCell-root:nth-child(1)': {
        paddingLeft: '1.313rem',
      },
      '& .MuiTableCell-root:nth-last-child(1)': {
        paddingRight: '1.883rem',
      },
    },
    '& .MuiTableBody-root': {
      '& :hover': {
        backgroundColor: '#f5faff',
      },
      '& > .MuiTableRow-root': {
        borderBottom: '1px solid #E6E8E9',
      },
      '& .MuiTableCell-root': {
        height: '3.5rem',
        fontFamily: 'NotoSans-Medium',
      },
      '& .MuiTableCell-root:nth-child(1)': {
        paddingLeft: '1.313rem',
      },
      '& .MuiTableCell-root:nth-last-child(1)': {
        paddingRight: '1.883rem',
      },
    },
  },
  status: {
    width: '0.625rem',
    height: '0.625rem',
    display: 'inline-block',
    backgroundColor: '#28d2ab',
    borderRadius: '50%',
    marginRight: '0.563rem',
  },
  editBtn: {
    width: '2.25rem',
    height: '2.25rem',
    minWidth: '0',
    backgroundColor: '#4c93ff',
    borderRadius: '0.625rem',
    color: '#fff',
    '&:hover': {
      background: '#4c93ff !important',
    },
  },
  editIcon: {
    fontSize: '1rem',
    '&:hover': {
      background: '#4c93ff !important',
    },
  },
  tableHead: {
    whiteSpace: 'nowrap',
  },
  add: {
    width: '5.688rem',
    height: '3rem',
    background: '#538fff',
    color: '#fff',
    borderRadius: '0.625rem',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
  },
  checkIcon: {
    width: '1.25rem',
    height: '1.25rem',
    color: '#28d2ab',
  },
  palette: {
    width: '1.375rem',
    height: '1.375rem',
    minWidth: '1.375rem',
    minHeight: '1.375rem',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
  },
}));

const pageSize = 20;
export default function CubicleRemarks() {
  const classes = useStyles();
  const [remarkList, setRemarkList] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    pageNo: 1,
    pageSize,
  });
  const [total, setTotal] = useState(0);
  const [edit, setEdit] = useState({
    open: false,
    data: {},
  });
  const [addPopup, setAddPopup] = useState(false);
  const tableRef = useRef();
  const rouList = useSelector((state) => state.rouList);

  const getRemarksListAPI = useCallback(async () => {
    try {
      const { result } = await bedStatusAPI.getCubicleRemarkList({ ...pageInfo });
      setTotal(result.count);
      if (pageInfo.pageNo > 1) {
        setRemarkList((o) => [...o, ...result.data]);
      } else {
        setRemarkList(result.data);
      }
    } catch (error) {}
  }, [pageInfo]);

  useEffect(() => {
    getRemarksListAPI();
  }, [getRemarksListAPI]);

  useUpdateEffect(() => {
    setPageInfo({
      pageNo: 1,
      pageSize,
    });
  }, [rouList]);

  const list = useMemo(() => {
    return _.map(remarkList, (item) => ({
      remark: item.content,
      active: Boolean(item.status),
      color: item.colour,
      id: item.remarkId,
    }));
  }, [remarkList]);

  // 打开 edit popup
  const handleOpenEditPopup = useCallback(
    (data) => () => {
      setEdit({
        open: true,
        data,
      });
    },
    []
  );

  // 关闭 edit popup
  const handleCloseEditPopup = useCallback(() => {
    setEdit({
      open: false,
      data: {},
    });
  }, []);

  // update cubicle remark
  const handleUpdateCubicleRemark = useCallback(async (data) => {
    try {
      await bedStatusAPI.updateCubicleRemark({
        colour: data.color,
        content: data.remark,
        remarkId: data.id,
        status: data.active ? 1 : 0,
      });
      setEdit({
        open: false,
        data: {},
      });
      setPageInfo({
        pageNo: 1,
        pageSize,
      });
    } catch (error) {}
  }, []);

  // 打开 add popup
  const handleOpenAddPopup = useCallback(() => {
    setAddPopup(true);
  }, []);

  // 关闭 add popup
  const handleCloseAddPopup = useCallback(() => {
    setAddPopup(false);
  }, []);

  // 添加 remark
  const hanldeCreateRemark = useCallback(async (data) => {
    try {
      await bedStatusAPI.createCubicleRemark({
        colour: data.color,
        content: data.remark,
        status: data.active ? 1 : 0,
      });
      setAddPopup(false);
      setPageInfo({
        pageNo: 1,
        pageSize,
      });
    } catch (error) {}
  }, []);

  // 翻页
  const turnPages = useCallback(() => {
    if (pageInfo.pageNo * pageInfo.pageSize < total) {
      setPageInfo((o) => ({
        ...o,
        pageNo: o.pageNo + 1,
      }));
    }
  }, [total, pageInfo]);

  const scrollHandler = useCallback(() => {
    if (tableRef) {
      const { scrollTop, clientHeight, scrollHeight } = tableRef.current;

      if (scrollTop + clientHeight + 1 >= scrollHeight) {
        turnPages();
      }
    }
  }, [turnPages]);

  return (
    <>
      <div className={classes.root}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonBase className={classes.add} id="bed_setting_add_btn1" onClick={handleOpenAddPopup}>
            <AddIcon style={{ fontSize: '1rem', marginRight: '0.313rem' }} />
            {i18n.t('BEDSTATUS_ADD')}
          </ButtonBase>
        </div>
        <TableContainer className={classes.tableContainer} ref={tableRef} onScroll={scrollHandler}>
          <Table className={classes.table} aria-label="simple table">
            <TableHead className={classes.tableHead} id="bed_setting_table_head">
              <TableRow>
                <TableCell align="left" style={{ wordBreak: 'break-all' }}>
                  {i18n.t('BEDSTATUS_REMARKS_CONTENT')}
                </TableCell>
                <TableCell align="center">{i18n.t('BEDSTATUS_ACTIVE')}</TableCell>
                <TableCell align="center">COLOUR</TableCell>
                <TableCell align="right">{i18n.t('BEDSTATUS_EDIT')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody} id="bed_setting_table_body">
              {list.map((row, rowIndex) => {
                const { remark, active, color } = row;
                return (
                  <TableRow
                    key={row.id}
                    onDoubleClick={handleOpenEditPopup(row)}
                    id={`bed_setting_table_row${rowIndex}`}
                  >
                    <TableCell align="left">{remark}</TableCell>
                    <TableCell align="center">{active ? <CheckIcon className={classes.checkIcon} /> : ''}</TableCell>
                    <TableCell
                      align="center"
                      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <div
                        className={classes.palette}
                        style={{
                          backgroundColor: BED_STATUS_REMARK_COLOR[color]?.bgColor,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <ButtonBase
                        className={classes.editBtn}
                        onClick={handleOpenEditPopup(row)}
                        id={`bed_setting_edit_btn${rowIndex}`}
                      >
                        <EditIcon className={classes.editIcon} />
                      </ButtonBase>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {addPopup && <AddRemark handleClose={handleCloseAddPopup} handleConfirm={hanldeCreateRemark} />}
      {edit.open && (
        <EditRemark handleClose={handleCloseEditPopup} handleConfirm={handleUpdateCubicleRemark} data={edit.data} />
      )}
    </>
  );
}
